import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { EditableTableColumn } from "../../components/common/EditableTable";
import { TableColumn } from "../../components/common/Table/Table";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import {
  CATEGORY_TYPE_MAP,
  DEFAULT_ACTION_OPTION,
  RATING_NOT_APPLICABLE,
  HUDDLES_OPTIONS,
} from "../../constants/pathToPerformance";
import { generateActionValue } from "../../utils/PathToPerformance/pathToPerformanceHelpers";
import {
  handleAsyncThunkError,
  deepCloneArray,
  getStatusBasedErrorMessage,
  extractErrorMessage,
} from "../../utils/sliceHelpers";
import {
  createDefaultPerformanceData,
  createDefaultActionItems,
  createDefaultAssessments,
} from "../../utils/PathToPerformance/p2pDataFactory";
import {
  updatePerformanceWithP2PData,
  updatePerformanceWithTeamData,
  syncPerformanceData,
  PERFORMANCE_DATA_INDEX,
} from "../../utils/PathToPerformance/sliceStateHelpers";


// Import types for local use within this slice
import type {
  AuthorizationResponse,
  CategoryActionItem,
  CategoryLevelsResponse,
  P2PDataResponse,
  TeamDetail,
  TeamAggregatedData,
  P2PSavePayload,
  TeamSettingsPayload,
  TeamSettingsResponse,
  DropdownOption,
  PerformanceData,
  ActionItemData,
  AssessmentData,
  PathToPerformanceState,
} from "../../types/pathToPerformance";

// Re-export all types for external consumers
export type {
  AuthorizationResponse,
  CategoryActionItem,
  CategoryLevelsResponse,
  CurrentP2PData,
  PreviousP2PData,
  P2PDataResponse,
  TeamDetail,
  TeamAggregatedData,
  P2PSavePayload,
  TeamSettingsPayload,
  TeamSettingsResponse,
  DropdownOption,
  PerformanceData,
  ActionItemData,
  AssessmentData,
  PathToPerformanceState,
} from "../../types/pathToPerformance";

// ============================================================================
// Configuration & Constants
// ============================================================================

/**
 * Dropdown options for huddles usage
 */
export const huddlesOptions: DropdownOption[] = [
  { id: 1, label: HUDDLES_OPTIONS.YES, value: HUDDLES_OPTIONS.YES },
  { id: 2, label: HUDDLES_OPTIONS.NO, value: HUDDLES_OPTIONS.NO },
];

// ============================================================================
// Helper Functions for Category Action Options (Using API Data)
// ============================================================================

/**
 * Transform category action items from API to dropdown options.
 * Automatically prepends default selection option.
 *
 * @param items - Array of category action items from API
 * @returns Array of dropdown options sorted by p2pOrder
 *
 * @example
 * ```typescript
 * const options = transformCategoryItemsToDropdownOptions(categoryLevels.S);
 * // Returns: [{ id: 0, label: "Select...", value: "select" }, ...]
 * ```
 */
export const transformCategoryItemsToDropdownOptions = (
  items: CategoryActionItem[],
): DropdownOption[] => {
  if (!items || items.length === 0) {
    return [{ id: 0, label: DEFAULT_ACTION_OPTION, value: "select" }];
  }

  // Start with the default "Select" option
  const options: DropdownOption[] = [
    { id: 0, label: DEFAULT_ACTION_OPTION, value: "select" },
  ];

  // Create sorted copy and transform to dropdown options
  const sortedItems = [...items].sort((a, b) => a.p2pOrder - b.p2pOrder);

  sortedItems.forEach((item) => {
    const levelText =
      item.actionNeededLvl === RATING_NOT_APPLICABLE
        ? "Level 99"
        : `Level ${item.actionNeededLvl}`;
    options.push({
      id: item.actionNeededId,
      label: `${item.actionNeededText} (${levelText})`,
      value: generateActionValue(item.actionNeededText, item.actionNeededLvl),
    });
  });

  return options;
};

/**
 * Get action options for a specific category from category levels data.
 * Maps user-friendly category names to API category codes.
 *
 * @param categoryLevels - Category levels data from API (from state.categoryLevels)
 * @param categoryName - Category name (Sponsorship, Leadership, Training, etc.)
 * @returns Array of dropdown options for the category
 *
 * @example
 * ```typescript
 * const sponsorOptions = getCategoryActionOptions(categoryLevels, "Sponsorship");
 * const leadershipOptions = getCategoryActionOptions(categoryLevels, "Leadership");
 * ```
 */
export const getCategoryActionOptions = (
  categoryLevels: CategoryLevelsResponse | null,
  categoryName: string,
): DropdownOption[] => {
  if (!categoryLevels) {
    return [{ id: 0, label: DEFAULT_ACTION_OPTION, value: "select" }];
  }

  // Map category names to API category codes (using centralized constant)
  const categoryKey = CATEGORY_TYPE_MAP[categoryName] as keyof CategoryLevelsResponse;
  if (!categoryKey || !categoryLevels[categoryKey]) {
    return [{ id: 0, label: DEFAULT_ACTION_OPTION, value: "select" }];
  }

  return transformCategoryItemsToDropdownOptions(categoryLevels[categoryKey]);
};

/**
 * Get all category action options as a map (dynamic version using API data).
 * Use this instead of hardcoded categoryActionOptionsMap.
 *
 * @param categoryLevels - Category levels data from API
 * @returns Map of category names to their dropdown options
 *
 * @example
 * ```typescript
 * const allOptions = getCategoryActionOptionsMap(categoryLevels);
 * const sponsorOptions = allOptions["Sponsorship"];
 * ```
 */
export const getCategoryActionOptionsMap = (
  categoryLevels: CategoryLevelsResponse | null,
): Record<string, DropdownOption[]> => {
  const categories = [
    "Sponsorship",
    "Leadership",
    "Training",
    "Team Process",
    "Team Engagement",
    "Use of Tools",
    "Goals and Performance",
  ];

  const optionsMap: Record<string, DropdownOption[]> = {};

  categories.forEach((category) => {
    optionsMap[category] = getCategoryActionOptions(categoryLevels, category);
  });

  return optionsMap;
};

/**
 * Dropdown options for assessment periods
 */
export const assessmentOptions: DropdownOption[] = [
  { id: 1, label: "Q1 2024", value: "q1-2024" },
  { id: 2, label: "Q2 2024", value: "q2-2024" },
];

/**
 * Performance data table column configuration
 */
export const performanceColumns: EditableTableColumn[] = [
  { key: "id", label: "ID" },
  { key: "ubtName", label: "Name of UBT", className: "md:max-w-[8rem]" },
  { key: "serviceType", label: "Service Type" },
  {
    key: "usingHuddles",
    label: "Using Huddles",
    isEdit: true,
    editType: "dropdown",
    options: huddlesOptions,
  },
  {
    key: "dateTeamEstablished",
    label: "Date Team Established",
    isEdit: false,
    editType: "date",
  },
  {
    key: "dateTeamClosed",
    label: "Date Team Closed",
    isEdit: false,
    editType: "date",
  },
  {
    key: "lastFaceToFaceAssessment",
    label: "Last Face to Face Assessment",
    isEdit: true,
    editType: "date",
  },
  { key: "teamRating", label: "Team Rating" },
];

/**
 * Action items table column configuration
 */
export const actionItemColumns: TableColumn[] = [
  { key: "category", label: "Category", className: "w-[12%]" },
  { key: "categoryLevel", label: "Category Level", className: "w-[9%]" },
  { key: "actionNeeded", label: "Action Needed", className: "w-[34%]" },
  { key: "notes", label: "Notes", className: "w-[45%]" },
];

/**
 * Assessment table column configuration
 */
export const assessmentColumns: TableColumn[] = [
  { key: "period", label: "Period", className: "md:max-w-[3rem]" },
  { key: "teamRating", label: "Team Rating", className: "md:max-w-[3.5rem]" },
  {
    key: "assessmentDate",
    label: "Assessment Date",
    className: "md:max-w-[4rem]",
  },
  {
    key: "assessmentNotes",
    label: "Assessment Notes",
    className: "md:max-w-[20rem]",
  },
];

// ============================================================================
// Initial State
// ============================================================================

const initialState: PathToPerformanceState = {
  teamId: null,
  authorization: null,
  categoryLevels: null,
  teamData: null,
  p2pData: null,
  performanceData: [createDefaultPerformanceData()],
  initialPerformanceData: [createDefaultPerformanceData()],
  actionItems: createDefaultActionItems(),
  initialActionItems: createDefaultActionItems(),
  actionNeededEdited: false,
  assessments: createDefaultAssessments(),
  initialAssessments: createDefaultAssessments(),
  hasUnsavedChanges: false,
  loading: false,
  error: null,
  validationError: null,
  successMessage: null,
  loadingAuth: false,
  loadingCategoryLevels: false,
  loadingP2PData: false,
  loadingTeamData: false,
  savingP2P: false,
  savingTeamSettings: false,
};

// ============================================================================
// Async Thunks (API Calls)
// ============================================================================

/**
 * Fetch authorization level for P2P access
 */
export const fetchP2PAuthorization = createAsyncThunk(
  "pathToPerformance/fetchAuthorization",
  async (teamId: number, { rejectWithValue, signal }) => {
    try {
      const response = await api.get<AuthorizationResponse>(
        endpoints.ubt.pathToPerformance.getAuthorization(teamId),
        { signal },
      );
      return response.data;
    } catch (error: any) {
      return handleAsyncThunkError(error, rejectWithValue, "Failed to fetch authorization");
    }
  },
);

/**
 * Fetch category levels and action needed options
 */
export const fetchCategoryLevels = createAsyncThunk(
  "pathToPerformance/fetchCategoryLevels",
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const response = await api.get<CategoryLevelsResponse>(
        endpoints.ubt.pathToPerformance.getCategoryLevels,
        { signal },
      );
      return response.data;
    } catch (error: any) {
      return handleAsyncThunkError(error, rejectWithValue, "Failed to fetch category levels");
    }
  },
);

/**
 * Fetch P2P data for a team
 */
export const fetchP2PData = createAsyncThunk(
  "pathToPerformance/fetchP2PData",
  async (teamId: number, { rejectWithValue, signal }) => {
    try {
      const response = await api.get<P2PDataResponse>(
        endpoints.ubt.pathToPerformance.getP2PData(teamId),
        { signal },
      );
      return response.data;
    } catch (error: any) {
      return handleAsyncThunkError(error, rejectWithValue, "Failed to fetch P2P data");
    }
  },
);

/**
 * Fetch aggregated team data
 * Returns a single TeamDetail object extracted from the API response
 */
export const fetchTeamAggregatedData = createAsyncThunk<
  TeamDetail | null,
  number,
  { rejectValue: string }
>(
  "pathToPerformance/fetchTeamAggregatedData",
  async (teamId: number, { rejectWithValue, signal }) => {
    try {
      const response = await api.get<TeamAggregatedData>(
        endpoints.ubt.members.getTeamMembers(teamId),
        { signal },
      );
      // Return first item from array (all items have same team info)
      if (response.data?.teamDetails?.length > 0) {
        return response.data.teamDetails[0];
      }
      return null;
    } catch (error: any) {
      return handleAsyncThunkError(error, rejectWithValue, "Failed to fetch team data");
    }
  },
);

/**
 * Save P2P data (POST or PATCH based on whether record exists)
 */
export const saveP2PData = createAsyncThunk(
  "pathToPerformance/saveP2PData",
  async (
    payload: { data: P2PSavePayload; isUpdate: boolean },
    { rejectWithValue },
  ) => {
    try {
      const endpoint = payload.isUpdate
        ? endpoints.ubt.pathToPerformance.updateP2P
        : endpoints.ubt.pathToPerformance.createP2P;

      const apiCall = payload.isUpdate ? api.patch : api.post;

      const response = await apiCall<{ success: boolean; message: string }>(
        endpoint,
        payload.data,
      );
      return response.data;
    } catch (error: any) {
      return handleAsyncThunkError(error, rejectWithValue, "Failed to save P2P data");
    }
  },
);

/**
 * Update team settings (learning plan refresh date, huddles usage)
 */
export const updateTeamSettings = createAsyncThunk(
  "pathToPerformance/updateTeamSettings",
  async (payload: TeamSettingsPayload, { rejectWithValue }) => {
    try {
      const response = await api.patch<TeamSettingsResponse>(
        endpoints.ubt.pathToPerformance.updateTeamSettings,
        payload,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = getStatusBasedErrorMessage(
        error,
        {
          401: "Invalid or missing x-nuid header",
          403: "Insufficient permissions to update team settings",
          500: "Failed to update team settings",
        },
        "Failed to update team settings",
      );
      return rejectWithValue(errorMessage);
    }
  },
);

// ============================================================================
// Slice Definition
// ============================================================================

const pathToPerformanceSlice = createSlice({
  name: "pathToPerformance",
  initialState,
  reducers: {
    // ========================================================================
    // Performance Data Reducers
    // ========================================================================

    /**
     * Replace all performance data
     */
    setPerformanceData: (state, action: PayloadAction<PerformanceData[]>) => {
      state.performanceData = action.payload;
    },

    /**
     * Update a single performance data item
     */
    updatePerformanceData: (
      state,
      action: PayloadAction<{ id: string; data: Partial<PerformanceData> }>,
    ) => {
      const index = state.performanceData.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.performanceData[index] = {
          ...state.performanceData[index],
          ...action.payload.data,
        };
      }
    },

    /**
     * Reset performance data to initial/original values from API
     */
    resetPerformanceData: (state) => {
      state.performanceData = deepCloneArray(state.initialPerformanceData);
    },

    /**
     * Set initial performance data (from API)
     */
    setInitialPerformanceData: (state, action: PayloadAction<PerformanceData[]>) => {
      state.initialPerformanceData = deepCloneArray(action.payload);
    },

    // ========================================================================
    // Action Items Reducers
    // ========================================================================

    /**
     * Replace all action items
     */
    setActionItems: (state, action: PayloadAction<ActionItemData[]>) => {
      state.actionItems = action.payload;
      state.actionNeededEdited = false;
    },

    /** Mark that the user explicitly changed an Action Needed dropdown. */
    setActionNeededEdited: (state, action: PayloadAction<boolean>) => {
      state.actionNeededEdited = action.payload;
    },

    /**
     * Update a single action item
     */
    updateActionItem: (
      state,
      action: PayloadAction<{ id: string; data: Partial<ActionItemData> }>,
    ) => {
      const index = state.actionItems.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.actionItems[index] = {
          ...state.actionItems[index],
          ...action.payload.data,
        };
      }
    },

    /**
     * Reset action items to initial/original values from API
     */
    resetActionItems: (state) => {
      state.actionItems = deepCloneArray(state.initialActionItems);
      state.actionNeededEdited = false;
    },

    /**
     * Set initial action items (from API)
     */
    setInitialActionItems: (state, action: PayloadAction<ActionItemData[]>) => {
      state.initialActionItems = deepCloneArray(action.payload);
      state.actionNeededEdited = false;
    },

    // ========================================================================
    // Assessment Reducers
    // ========================================================================

    /**
     * Replace all assessments
     */
    setAssessments: (state, action: PayloadAction<AssessmentData[]>) => {
      state.assessments = action.payload;
    },

    /**
     * Update a single assessment
     */
    updateAssessment: (
      state,
      action: PayloadAction<{ id: string; data: Partial<AssessmentData> }>,
    ) => {
      const index = state.assessments.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.assessments[index] = {
          ...state.assessments[index],
          ...action.payload.data,
        };
      }
    },

    /**
     * Reset assessments to initial/original values from API
     */
    resetAssessments: (state) => {
      state.assessments = deepCloneArray(state.initialAssessments);
    },

    /**
     * Set initial assessments (from API)
     */
    setInitialAssessments: (state, action: PayloadAction<AssessmentData[]>) => {
      state.initialAssessments = deepCloneArray(action.payload);
    },

    // ========================================================================
    // General State Reducers
    // ========================================================================

    /**
     * Set team ID
     */
    setTeamId: (state, action: PayloadAction<number | null>) => {
      state.teamId = action.payload;
    },

    /**
     * Set authorization data
     */
    setAuthorization: (
      state,
      action: PayloadAction<AuthorizationResponse | null>,
    ) => {
      state.authorization = action.payload;
    },

    /**
     * Set success message state
     */
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },

    /**
     * Set unsaved changes flag
     */
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    /**
     * Set error state (API errors - auto-dismiss)
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    /**
     * Set validation error state (persistent)
     */
    setValidationError: (state, action: PayloadAction<string | null>) => {
      state.validationError = action.payload;
    },

    /**
     * Reset entire state to initial values
     */
    resetPathToPerformance: () => initialState,
  },
  // ============================================================================
  // Extra Reducers (Async Thunks)
  // ============================================================================
  extraReducers: (builder) => {
    // ========================================================================
    // Fetch Authorization
    // ========================================================================
    builder
      .addCase(fetchP2PAuthorization.pending, (state) => {
        state.loadingAuth = true;
        state.error = null;
      })
      .addCase(fetchP2PAuthorization.fulfilled, (state, action) => {
        // Ignore a late response for a team the user has since navigated away from.
        if (action.meta.arg !== state.teamId) return;
        state.loadingAuth = false;
        state.authorization = action.payload;
      })
      .addCase(fetchP2PAuthorization.rejected, (state, action) => {
        if (action.meta.arg !== state.teamId) return;
        state.loadingAuth = false;
        state.error = extractErrorMessage(action.payload);
      });

    // ========================================================================
    // Fetch Category Levels
    // ========================================================================
    builder
      .addCase(fetchCategoryLevels.pending, (state) => {
        state.loadingCategoryLevels = true;
        state.error = null;
      })
      .addCase(fetchCategoryLevels.fulfilled, (state, action) => {
        state.loadingCategoryLevels = false;
        state.categoryLevels = action.payload;
      })
      .addCase(fetchCategoryLevels.rejected, (state, action) => {
        state.loadingCategoryLevels = false;
        state.error = extractErrorMessage(action.payload);
      });

    // ========================================================================
    // Fetch P2P Data
    // ========================================================================
    builder
      .addCase(fetchP2PData.pending, (state) => {
        state.loadingP2PData = true;
        state.error = null;
      })
      .addCase(fetchP2PData.fulfilled, (state, action) => {
        // Ignore a late response for a team the user has since navigated away from.
        if (action.meta.arg !== state.teamId) return;
        state.loadingP2PData = false;
        state.p2pData = action.payload;

        // Update performance data from P2P current data using helper
        if (action.payload?.current) {
          syncPerformanceData(
            state.performanceData,
            state.initialPerformanceData,
            (data) => updatePerformanceWithP2PData(data, action.payload.current)
          );
        }
      })
      .addCase(fetchP2PData.rejected, (state, action) => {
        if (action.meta.arg !== state.teamId) return;
        state.loadingP2PData = false;
        state.error = extractErrorMessage(action.payload);
      });

    // ========================================================================
    // Fetch Team Aggregated Data
    // ========================================================================
    builder
      .addCase(fetchTeamAggregatedData.pending, (state) => {
        state.loadingTeamData = true;
        state.error = null;
      })
      .addCase(fetchTeamAggregatedData.fulfilled, (state, action) => {
        // Ignore a late response for a team the user has since navigated away from.
        if (action.meta.arg !== state.teamId) return;
        state.loadingTeamData = false;
        state.teamData = action.payload;

        // Update performance data from team data using helper
        if (action.payload) {
          const teamData = action.payload; // Type assertion for non-null
          syncPerformanceData(
            state.performanceData,
            state.initialPerformanceData,
            (data) => updatePerformanceWithTeamData(data, teamData)
          );
        }
      })
      .addCase(fetchTeamAggregatedData.rejected, (state, action) => {
        if (action.meta.arg !== state.teamId) return;
        state.loadingTeamData = false;
        state.error = extractErrorMessage(action.payload);
      });

    // ========================================================================
    // Save P2P Data
    // ========================================================================
    builder
      .addCase(saveP2PData.pending, (state) => {
        state.savingP2P = true;
        state.error = null;
      })
      .addCase(saveP2PData.fulfilled, (state) => {
        state.savingP2P = false;
        state.hasUnsavedChanges = false;
      })
      .addCase(saveP2PData.rejected, (state, action) => {
        state.savingP2P = false;
        state.error = extractErrorMessage(action.payload);
      });

    // ========================================================================
    // Update Team Settings
    // ========================================================================
    builder
      .addCase(updateTeamSettings.pending, (state) => {
        state.savingTeamSettings = true;
        state.error = null;
      })
      .addCase(updateTeamSettings.fulfilled, (state) => {
        state.savingTeamSettings = false;
        state.hasUnsavedChanges = false;
        
        // Update initial data to reflect saved changes
        const initial = state.initialPerformanceData[PERFORMANCE_DATA_INDEX];
        const current = state.performanceData[PERFORMANCE_DATA_INDEX];
        
        if (initial && current) {
          initial.lastFaceToFaceAssessment = current.lastFaceToFaceAssessment;
          initial.usingHuddles = current.usingHuddles;
        }
      })
      .addCase(updateTeamSettings.rejected, (state, action) => {
        state.savingTeamSettings = false;
        state.error = extractErrorMessage(action.payload);
      });
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  // Team ID
  setTeamId,

  // Authorization
  setAuthorization,

  // Unsaved changes
  setHasUnsavedChanges,

  // Performance data actions
  setPerformanceData,
  setInitialPerformanceData,
  updatePerformanceData,
  resetPerformanceData,

  // Action items actions
  setActionItems,
  setActionNeededEdited,
  setInitialActionItems,
  updateActionItem,
  resetActionItems,

  // Assessment actions
  setAssessments,
  setInitialAssessments,
  updateAssessment,
  resetAssessments,

  // General actions
  setLoading,
  setError,
  setValidationError,
  setSuccessMessage,
  resetPathToPerformance,
} = pathToPerformanceSlice.actions;

export default pathToPerformanceSlice.reducer;
